import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { MmiProjection } from '@app/finance';
import { TAX_RESIDENCIES, type Assumptions, type ProjectionInput } from '@app/shared';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatEuros, formatRatio } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { percentToRatio, ratioToPercent } from '@/lib/rate';
import { computeProjection } from './engine';
import { EngineSelect, useStoredEngine } from './EngineSelect';
import { useCreateSimulation } from './queries';
import { TAX_RESIDENCY_LABELS } from './labels';
import { Field } from './Field';
import { ProjectionChart } from './ProjectionChart';

const formSchema = z.object({
  taxResidency: z.enum(TAX_RESIDENCIES),
  surfaceM2: z.number().positive('Requis'),
  holdingYears: z.number().int().min(1).max(35),
  purchasePrice: z.number().positive('Requis'),
  notaryFees: z.number().min(0),
  works: z.number().min(0),
  plan: z.number().min(0),
  furniture: z.number().min(0),
  agencyFees: z.number().min(0),
  guaranteeFees: z.number().min(0),
  monthlyRent: z.number().min(0),
  vacancyMonths: z.number().min(0).max(12),
  monthlyCondo: z.number().min(0),
  monthlyPropertyTax: z.number().min(0),
  monthlyCfe: z.number().min(0),
  monthlyPno: z.number().min(0),
  monthlyAccounting: z.number().min(0),
  monthlyEnergy: z.number().min(0),
  monthlyInternet: z.number().min(0),
  managementFeeRate: z.number().min(0).max(100),
  debt: z.number().min(0),
  loanAnnualRate: z.number().min(0).max(25),
  loanYears: z.number().int().min(0).max(60),
  loanInsuranceMonthly: z.number().min(0),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  taxResidency: 'non-resident-eea',
  surfaceM2: 50,
  holdingYears: 25,
  purchasePrice: 200_000,
  notaryFees: 16_000,
  works: 0,
  plan: 0,
  furniture: 0,
  agencyFees: 0,
  guaranteeFees: 0,
  monthlyRent: 1_200,
  vacancyMonths: 0,
  monthlyCondo: 0,
  monthlyPropertyTax: 0,
  monthlyCfe: 0,
  monthlyPno: 0,
  monthlyAccounting: 0,
  monthlyEnergy: 0,
  monthlyInternet: 0,
  managementFeeRate: 0,
  debt: 160_000,
  loanAnnualRate: ratioToPercent(0.02),
  loanYears: 20,
  loanInsuranceMonthly: 20,
};

function toInput(values: FormValues): ProjectionInput {
  return {
    ...values,
    managementFeeRate: percentToRatio(values.managementFeeRate),
    loanAnnualRate: percentToRatio(values.loanAnnualRate),
  };
}

function KpiCards({ projection }: { projection: MmiProjection }) {
  const items: { label: string; value: string }[] = [
    { label: 'TRI', value: formatRatio(projection.kpi.irr) },
    { label: 'Cash-flow mensuel (année 1)', value: formatEuros(projection.kpi.monthlyCashFlow) },
    { label: 'Rendement brut', value: formatRatio(projection.kpi.grossYield) },
    { label: 'Rendement net', value: formatRatio(projection.kpi.netYield) },
    { label: 'Retour sur investissement', value: projection.kpi.paybackYears === null ? 'n.a' : `${projection.kpi.paybackYears.toFixed(1)} ans` },
    { label: 'Taux de couverture', value: formatRatio(projection.kpi.coverageRatio) },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {items.map((item) => (
        <Card key={item.label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-normal text-muted-foreground">{item.label}</CardTitle>
          </CardHeader>
          <CardContent className="text-lg font-semibold">{item.value}</CardContent>
        </Card>
      ))}
    </div>
  );
}

function AnnualTable({ projection }: { projection: MmiProjection }) {
  const lastYear = projection.summary.saleYear - projection.years[0]!;
  const rows = Array.from({ length: lastYear }, (_, index) => index + 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="p-2 text-left font-medium text-muted-foreground">Année</th>
            <th className="p-2 text-right font-medium text-muted-foreground">Loyer</th>
            <th className="p-2 text-right font-medium text-muted-foreground">Charges</th>
            <th className="p-2 text-right font-medium text-muted-foreground">Intérêts</th>
            <th className="p-2 text-right font-medium text-muted-foreground">Impôt</th>
            <th className="p-2 text-right font-medium text-muted-foreground">Cash-flow</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((n) => {
            const cf = projection.cashFlow;
            const charges =
              (cf.managementFees[n] ?? 0) +
              (cf.condo[n] ?? 0) +
              (cf.propertyTax[n] ?? 0) +
              (cf.cfe[n] ?? 0) +
              (cf.pno[n] ?? 0) +
              (cf.accounting[n] ?? 0) +
              (cf.energy[n] ?? 0) +
              (cf.internet[n] ?? 0);
            return (
              <tr key={n} className="border-b border-border/50">
                <td className="p-2">{projection.years[n]}</td>
                <td className="p-2 text-right tabular-nums">{formatEuros(cf.rent[n] ?? 0)}</td>
                <td className="p-2 text-right tabular-nums">{formatEuros(charges)}</td>
                <td className="p-2 text-right tabular-nums">{formatEuros(cf.interest[n] ?? 0)}</td>
                <td className="p-2 text-right tabular-nums">{formatEuros(cf.incomeTax[n] ?? 0)}</td>
                <td className="p-2 text-right tabular-nums font-medium">{formatEuros(cf.total[n] ?? 0)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ProjectionForm({ assumptions }: { assumptions: Assumptions }) {
  const [label, setLabel] = useState('Projection — ');
  const [engine, setEngine] = useStoredEngine();
  const { register, control } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });
  const values = useWatch({ control });
  const createSimulation = useCreateSimulation();

  const projection = useMemo(() => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) return null;
    try {
      return computeProjection(toInput(parsed.data), assumptions, engine);
    } catch {
      return null;
    }
  }, [values, assumptions, engine]);

  const handleSave = () => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      toast.error('Corrigez les champs invalides avant d’enregistrer.');
      return;
    }
    createSimulation.mutate(
      { kind: 'projection', label: label.trim() || 'Projection', engineVersion: engine, input: toInput(parsed.data) },
      {
        onSuccess: () => toast.success('Simulation enregistrée'),
        onError: (error) => toast.error(apiErrorMessage(error, 'Échec de l’enregistrement')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-4">
        <EngineSelect value={engine} onChange={setEngine} />
        <Field label="Résidence fiscale" htmlFor="taxResidency">
          <Controller
            control={control}
            name="taxResidency"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full max-w-md" id="taxResidency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAX_RESIDENCIES.map((residency) => (
                    <SelectItem key={residency} value={residency}>
                      {TAX_RESIDENCY_LABELS[residency]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Investissement initial</legend>
          <Field label="Surface (m²)" htmlFor="surfaceM2">
            <Input id="surfaceM2" type="number" step="0.1" min="0" {...register('surfaceM2', { valueAsNumber: true })} />
          </Field>
          <Field label="Durée de détention (années)" htmlFor="holdingYears">
            <Input id="holdingYears" type="number" step="1" min="1" max="35" {...register('holdingYears', { valueAsNumber: true })} />
          </Field>
          <Field label="Prix d'acquisition (€)" htmlFor="purchasePrice">
            <Input id="purchasePrice" type="number" step="1" min="0" {...register('purchasePrice', { valueAsNumber: true })} />
          </Field>
          <Field label="Frais de notaire (€)" htmlFor="notaryFees">
            <Input id="notaryFees" type="number" step="1" min="0" {...register('notaryFees', { valueAsNumber: true })} />
          </Field>
          <Field label="Travaux (€)" htmlFor="works">
            <Input id="works" type="number" step="1" min="0" {...register('works', { valueAsNumber: true })} />
          </Field>
          <Field label="Plans (€)" htmlFor="plan">
            <Input id="plan" type="number" step="1" min="0" {...register('plan', { valueAsNumber: true })} />
          </Field>
          <Field label="Mobilier (€)" htmlFor="furniture">
            <Input id="furniture" type="number" step="1" min="0" {...register('furniture', { valueAsNumber: true })} />
          </Field>
          <Field label="Honoraires d'agence (€)" htmlFor="agencyFees">
            <Input id="agencyFees" type="number" step="1" min="0" {...register('agencyFees', { valueAsNumber: true })} />
          </Field>
          <Field label="Frais de garantie (€)" htmlFor="guaranteeFees">
            <Input id="guaranteeFees" type="number" step="1" min="0" {...register('guaranteeFees', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Exploitation</legend>
          <Field label="Loyer mensuel (€)" htmlFor="monthlyRent">
            <Input id="monthlyRent" type="number" step="1" min="0" {...register('monthlyRent', { valueAsNumber: true })} />
          </Field>
          <Field label="Vacance locative (mois/an)" htmlFor="vacancyMonths">
            <Input id="vacancyMonths" type="number" step="0.1" min="0" max="12" {...register('vacancyMonths', { valueAsNumber: true })} />
          </Field>
          <Field label="Charges de gestion (%)" htmlFor="managementFeeRate">
            <Input id="managementFeeRate" type="number" step="0.1" min="0" {...register('managementFeeRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Charges de copropriété mensuelles (€)" htmlFor="monthlyCondo">
            <Input id="monthlyCondo" type="number" step="1" min="0" {...register('monthlyCondo', { valueAsNumber: true })} />
          </Field>
          <Field label="Taxe foncière mensuelle (€)" htmlFor="monthlyPropertyTax">
            <Input id="monthlyPropertyTax" type="number" step="1" min="0" {...register('monthlyPropertyTax', { valueAsNumber: true })} />
          </Field>
          <Field label="CFE mensuelle (€)" htmlFor="monthlyCfe">
            <Input id="monthlyCfe" type="number" step="1" min="0" {...register('monthlyCfe', { valueAsNumber: true })} />
          </Field>
          <Field label="Assurance PNO mensuelle (€)" htmlFor="monthlyPno">
            <Input id="monthlyPno" type="number" step="1" min="0" {...register('monthlyPno', { valueAsNumber: true })} />
          </Field>
          <Field label="Comptabilité mensuelle (€)" htmlFor="monthlyAccounting">
            <Input id="monthlyAccounting" type="number" step="1" min="0" {...register('monthlyAccounting', { valueAsNumber: true })} />
          </Field>
          <Field label="Électricité, eau, gaz mensuels (€)" htmlFor="monthlyEnergy">
            <Input id="monthlyEnergy" type="number" step="1" min="0" {...register('monthlyEnergy', { valueAsNumber: true })} />
          </Field>
          <Field label="Internet et TV mensuels (€)" htmlFor="monthlyInternet">
            <Input id="monthlyInternet" type="number" step="1" min="0" {...register('monthlyInternet', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Financement</legend>
          <Field label="Dette bancaire (€)" htmlFor="debt">
            <Input id="debt" type="number" step="1" min="0" {...register('debt', { valueAsNumber: true })} />
          </Field>
          <Field label="Taux annuel, assurance incluse (%)" htmlFor="loanAnnualRate">
            <Input id="loanAnnualRate" type="number" step="0.01" min="0" {...register('loanAnnualRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Durée du prêt (années)" htmlFor="loanYears">
            <Input id="loanYears" type="number" step="1" min="0" max="60" {...register('loanYears', { valueAsNumber: true })} />
          </Field>
          <Field label="Assurance emprunteur mensuelle (€)" htmlFor="loanInsuranceMonthly">
            <Input id="loanInsuranceMonthly" type="number" step="1" min="0" {...register('loanInsuranceMonthly', { valueAsNumber: true })} />
          </Field>
        </fieldset>
      </form>

      {projection ? (
        <div className="flex flex-col gap-6">
          <KpiCards projection={projection} />
          <ProjectionChart projection={projection} />
          <AnnualTable projection={projection} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Corrigez les champs invalides pour voir le résultat.</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Nom de la simulation" htmlFor="label">
          <Input id="label" value={label} onChange={(event) => setLabel(event.target.value)} className="w-64" />
        </Field>
        <Button onClick={handleSave} disabled={createSimulation.isPending || !projection}>
          {createSimulation.isPending ? 'Enregistrement…' : 'Enregistrer cette simulation'}
        </Button>
      </div>
    </div>
  );
}
